import type { NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { AboutContextProvider } from '@/contexts/AboutContext'
import { AboutTile } from '@/contexts/AboutContext'
import { Seo } from '@/layout/Seo'
import { GUILD_MEMBERSHIP_NUMBERS_ID, OUR_IMPACT_ENTRY_ID, getCollection } from '@/services/CmsService/Collection'
import { getContentCollections } from '@/services/CollectionsService/CollectionsService'
import { ContentCollection } from '@/services/CollectionsService/types'
import { Collection } from '@/types/codegen-contentful'
import { isStagingEnvironment } from '@/utils/environment-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { isDefined } from '@/utils/primitives/generics'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { AboutView } from '@/views/AboutView'

const ABOUT_TITLES_COLLECTION_ID = isStagingEnvironment()
  ? '45ea14dc-5864-46da-998f-1da200e8e004'
  : '97d45d0e-1f18-427b-9fd1-44ad07306dea'

function extractAboutTiles(collection: ContentCollection | null | undefined): AboutTile[] {
  if (!collection?.connection?.edges) return []

  return collection.connection.edges
    .map((edge) => {
      const node = edge?.node
      if (!node) return null

      const isSeriesOrMovie = node.__typename === 'ContentSeries' || node.__typename === 'ContentMovie'
      if (!isSeriesOrMovie) return null

      const slug = node.project?.slug
      const name = node.name
      const imageCloudinaryPath = node.portraitAngelImage?.cloudinaryPath ?? node.posterTitleImage?.cloudinaryPath
      const projectType = node.__typename === 'ContentSeries' ? 'series' : 'movie'

      if (!slug || !name || !imageCloudinaryPath) return null

      return { slug, name, imageCloudinaryPath, projectType }
    })
    .filter(isDefined)
}

interface AboutProps {
  impact: Collection
  locale: string
  preview: boolean
  tiles: AboutTile[]
  members: string | null
  lastUpdated: string | null
  guildMembershipNumbers: Collection
}

const Home: NextPage<AboutProps> = ({ impact, locale, tiles, members, lastUpdated, guildMembershipNumbers }) => {
  const { t } = useTranslate('about')
  const basePath = `${getBasePath(locale)}`
  const url = `${basePath}${paths.about.index}`
  const title = t('angelAboutTitle', 'About Angel | Angel')
  const description = t(
    'angelAboutDescription',
    'Angel is a leading crowd-funded streaming service and film studio whose mission is to create stories that amplify the light. Learn more about us here.',
  )

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.about.index}
        title={title}
      />
      <AboutContextProvider
        impact={impact}
        tiles={tiles}
        numberOfGuildMembers={members}
        lastUpdated={lastUpdated}
        guildMembershipNumbers={guildMembershipNumbers}
      >
        <AboutView />
      </AboutContextProvider>
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<AboutProps>(
  { filePath: 'pages/[locale]/about/index.tsx' },
  async ({ federationClient, locale, preview, next404 }) => {
    const [impact, guildMembershipNumbers, collections, translations] = await Promise.all([
      getCollection({ locale, preview }, OUR_IMPACT_ENTRY_ID),
      getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID),
      getContentCollections([ABOUT_TITLES_COLLECTION_ID], federationClient),
      loadTranslations(locale, ['common', 'about', 'app-promo', 'account']),
    ])

    if (!impact || !guildMembershipNumbers) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    const tiles = extractAboutTiles(collections[0])
    const members = guildMembershipNumbers.customData[0]?.numSubscribers ?? null
    const lastUpdated = guildMembershipNumbers.customData[0]?.updatedAt ?? null

    return {
      props: {
        impact,
        locale,
        preview,
        tiles,
        members,
        lastUpdated,
        guildMembershipNumbers,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default Home
